// Terms of Service / Terms of Use — public-facing legal page that
// pairs with /privacy. Standard Canadian mortgage-broker boilerplate
// covering: independent broker status, no warranty on rate accuracy,
// RECA licensing, dispute jurisdiction (Alberta), and the limited
// scope of services provided through hellomortgagerates.ca.
//
// Like the privacy page, this exists primarily as a TRUST SIGNAL for
// Google's Safe Browsing classifier — sites that collect personal
// information without a published Terms of Service are pattern-matched
// against deceptive aggregators.
//
// IMPORTANT: this is a strong starting draft based on standard FSRA /
// RECA broker templates, but is NOT legal advice. Matt should review
// it with his BRX compliance contact before going live and adjust any
// language that doesn't fit his specific operating model.
import React, { useEffect } from "react";
import PublicFooter from "./PublicFooter";

const CONTACT = {
  email: "sayhello@hellomortgage.ca",
  phone: "780-900-2495",
};

export default function TermsOfService() {
  useEffect(() => {
    document.title = "Terms of Service — Hello Mortgage";
    const meta = document.querySelector('meta[name="description"]') || (() => {
      const m = document.createElement("meta");
      m.setAttribute("name", "description");
      document.head.appendChild(m);
      return m;
    })();
    meta.setAttribute(
      "content",
      "Hello Mortgage Terms of Service. Independent Alberta mortgage brokerage operated by Matt Broom-Hall, licensed by RECA in affiliation with BRX Mortgage."
    );
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <article
        className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-14"
        data-testid="terms-of-service-page"
      >
        <header className="mb-8">
          <a
            href="/"
            className="text-xs font-bold uppercase tracking-[0.18em] text-brand-red hover:text-brand-navy transition-colors font-body"
            data-testid="terms-back"
          >
            ← Hello Mortgage
          </a>
          <h1 className="text-4xl sm:text-5xl font-display text-brand-navy mt-3 mb-2">
            Terms of Service
          </h1>
          <p className="text-base text-slate-600 font-body">
            Last updated:{" "}
            {new Date().toLocaleDateString("en-CA", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
        </header>

        <div className="space-y-5 text-slate-700 font-body leading-relaxed text-[15px]">
          <Section title="1. Who we are">
            <p>
              Hello Mortgage (also operating as "Hello Mortgage Team", and
              together with the websites <code>hellomortgage.ca</code> and{" "}
              <code>hellomortgagerates.ca</code>, the "Site") is an independent
              mortgage brokerage operated by{" "}
              <strong>Matt Broom-Hall</strong>, Mortgage Broker &amp; Owner,
              licensed by the <strong>Real Estate Council of Alberta (RECA)</strong>,
              in affiliation with <strong>BRX Mortgage</strong> (the "Brokerage").
              The Brokerage's office of record is{" "}
              500 4th Avenue SW, Suite 2500, Calgary, AB T2P 2V6.
            </p>
            <p>
              We are <strong>not a lender</strong>. We do not issue, fund, or
              underwrite mortgages directly. We compare rates and product
              policies across 26+ Canadian mortgage lenders and present the
              best available options to our clients.
            </p>
          </Section>

          <Section title="2. Acceptance of these terms">
            <p>
              By accessing or using the Site, you agree to be bound by these
              Terms of Service ("Terms") and our{" "}
              <a
                href="/privacy"
                className="text-brand-red font-medium hover:underline"
              >
                Privacy Policy
              </a>
              . If you do not agree, please do not use the Site.
            </p>
            <p>
              We may update these Terms from time to time. Continued use of the
              Site after a change is published constitutes acceptance of the
              revised Terms. The "Last updated" date at the top of this page
              tells you when the Terms were most recently changed.
            </p>
          </Section>

          <Section title="3. Rate information is for guidance only">
            <p>
              Mortgage rates displayed on the Site are aggregated from rate
              sheets provided by lenders. While we make every reasonable effort
              to keep them accurate and current, rates can change at any time
              without notice and are subject to:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Lender qualification, approval, and underwriting</li>
              <li>
                Property type, occupancy, down payment, and loan-to-value ratio
              </li>
              <li>
                Borrower credit, income verification, and debt-service ratios
              </li>
              <li>
                Default insurance eligibility (CMHC, Sagen, Canada Guaranty)
              </li>
              <li>Lender-specific rate-hold and commitment policies</li>
            </ul>
            <p>
              Rate information is presented "as is" and "as available" with no
              warranty of any kind. <strong>OAC. E&amp;OE.</strong>{" "}
              ("On Approved Credit. Errors and Omissions Excepted.") Always
              confirm rate, terms, and qualification with us in writing before
              relying on any figure shown.
            </p>
          </Section>

          <Section title="4. Not financial, legal, or tax advice">
            <p>
              Content on the Site — including blog posts, market commentary,
              the Variable-to-Fixed Switch Analyzer, the Hello Mortgage Insights
              briefing, and any rate predictions or forecasts — is provided for
              <strong> educational and informational purposes only</strong>. It
              is not, and should not be taken as, financial, investment, legal,
              tax, or accounting advice.
            </p>
            <p>
              Mortgage decisions depend on your personal circumstances. Before
              acting on anything you read on the Site, speak with us directly,
              and where appropriate consult a qualified financial planner,
              accountant, or lawyer.
            </p>
          </Section>

          <Section title="5. The Switch Analyzer and other modelling tools">
            <p>
              The Site includes interactive tools — including but not limited
              to the Variable-to-Fixed Switch Analyzer, mortgage payment
              calculators, and rate prediction features — that model possible
              future outcomes based on inputs you provide and on publicly
              available data sources (Bank of Canada announcements, Government
              of Canada bond yields, lender rate sheets, and broker-collected
              economist commentary).
            </p>
            <p>
              These tools generate <strong>illustrative scenarios, not forecasts</strong>.
              We model 12 months of forward rate movement and explicitly
              disclose that we do not model beyond that window. No model can
              accurately predict future interest rates, real estate values, or
              individual financial outcomes. Use the tools as a starting point
              for conversation, not as a basis for irreversible decisions.
            </p>
          </Section>

          <Section title="6. How we are compensated">
            <p>
              We are typically compensated by the lender that funds your
              mortgage, not by you directly. The amount of compensation can
              vary by lender and product. Where required by law, regulation,
              or RECA rules of conduct, the specific compensation arrangement
              will be disclosed to you before you commit to a mortgage. We
              never receive a referral fee in exchange for steering you to a
              particular lender.
            </p>
          </Section>

          <Section title="7. Lender names and logos">
            <p>
              Lender names, logos, and trademarks appearing on the Site are the
              property of their respective owners and are displayed solely for
              the purpose of identifying mortgage products and rate sources in a
              comparison context. Their appearance does not imply affiliation,
              sponsorship, or endorsement by those lenders. Hello Mortgage is
              not affiliated with or controlled by any individual lender.
            </p>
          </Section>

          <Section title="8. Personal information">
            <p>
              Information you submit through any form on the Site (including
              name, email, phone number, mortgage details, and property
              information) is handled in accordance with our{" "}
              <a
                href="/privacy"
                className="text-brand-red font-medium hover:underline"
              >
                Privacy Policy
              </a>
              , which complies with PIPEDA and the ten Canadian Privacy
              Principles. We do <strong>not</strong> sell your personal
              information to third parties. We share it only with lenders and
              service providers directly involved in the mortgage transaction
              you have asked us to facilitate, and only after obtaining your
              consent.
            </p>
          </Section>

          <Section title="9. Acceptable use">
            <p>You agree not to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                Use the Site or any information from it to harass, defraud, or
                impersonate another person.
              </li>
              <li>
                Submit false, misleading, or unauthorized information through
                any form.
              </li>
              <li>
                Scrape, copy, redistribute, or commercialize Site content
                without our prior written consent.
              </li>
              <li>
                Attempt to interfere with the security, integrity, or
                availability of the Site.
              </li>
              <li>Violate any applicable Canadian law in connection with the Site.</li>
            </ul>
          </Section>

          <Section title="10. Intellectual property">
            <p>
              All content on the Site — including text, graphics, design,
              software, blog posts, and the structure and selection of rate
              data — is owned by or licensed to Hello Mortgage and is protected
              by Canadian and international copyright laws. You may share
              individual blog posts and rate snapshots with attribution, but
              you may not republish substantial portions of the Site without
              our written permission.
            </p>
          </Section>

          <Section title="11. Limitation of liability">
            <p>
              To the fullest extent permitted by law, Hello Mortgage, its
              owners, employees, and affiliates (including BRX Mortgage) are
              not liable for any indirect, incidental, consequential, special,
              or punitive damages arising from your use of, or inability to
              use, the Site or any information provided through it. This
              includes — without limiting the foregoing — losses caused by
              reliance on rate information that has changed, on modelling
              tools that produced an outcome different from reality, or on
              third-party content linked from the Site.
            </p>
          </Section>

          <Section title="12. Third-party links and content">
            <p>
              The Site may link to or embed content from third parties
              (including the Bank of Canada, lenders, news outlets, and social
              platforms). We are not responsible for the content, privacy
              practices, or availability of those third-party sites and
              services.
            </p>
          </Section>

          <Section title="13. Governing law and dispute resolution">
            <p>
              These Terms are governed by the laws of the Province of Alberta
              and the federal laws of Canada applicable in Alberta. Any dispute
              arising out of or relating to the Site or these Terms will be
              resolved in the courts located in Calgary, Alberta, and you
              consent to the exclusive jurisdiction of those courts.
            </p>
            <p>
              Nothing in these Terms limits any consumer-protection right you
              have under applicable Canadian or Alberta law that cannot be
              waived by contract.
            </p>
          </Section>

          <Section title="14. Contact">
            <p>If you have questions about these Terms, please contact us:</p>
            <div
              className="bg-slate-50 border border-slate-200 rounded-xl p-5 mt-2"
              data-testid="terms-contact"
            >
              <div className="font-body text-slate-800 text-sm space-y-1">
                <div>Matt Broom-Hall · Mortgage Broker &amp; Owner</div>
                <div>
                  Email:{" "}
                  <a
                    href={`mailto:${CONTACT.email}`}
                    className="text-brand-red font-medium hover:underline"
                  >
                    {CONTACT.email}
                  </a>
                </div>
                <div>
                  Phone:{" "}
                  <a
                    href={`tel:+1${CONTACT.phone.replace(/\D/g, "")}`}
                    className="text-brand-red font-medium hover:underline"
                  >
                    {CONTACT.phone}
                  </a>
                </div>
                <div className="text-slate-600 text-xs pt-1">
                  Hello Mortgage · powered by BRX Mortgage · Calgary, Alberta
                </div>
              </div>
            </div>
          </Section>
        </div>

        <PublicFooter />
      </article>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="space-y-3">
      <h2 className="text-xl font-display text-brand-navy pt-4">{title}</h2>
      {children}
    </section>
  );
}
